import { DragEndEvent } from '@dnd-kit/core';
import { Canceler } from 'axios';
import _ from 'lodash';
import { action, computed, makeObservable, observable } from 'mobx';
import { ITEM_TYPE, LOCALES, MEASURE, defaultPagination } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { TCategoryProduct } from 'src/types/categories';
import { ICountsResponce, IPagination, TBooleanInNumber, TTranslate } from 'src/types/main';
import {
  IProductNutrient,
  IProductV4,
  IProductV4Exclusion,
  IProductV4Modifier,
  IProductV4ModifierOption,
  IProductV4Size,
  PRODUCT_ORDER_TYPE,
  PRODUCT_TYPE,
} from 'src/types/product';
import {
  booleanNumberReverse,
  booleanToNumber,
  countsUpdate,
  getAvailabilityParams,
  getCancelToken,
  getTranslate,
  isOrderTypeSupported,
  randomFloatNumber,
  scrollToErrorElement,
} from 'src/utils/helpers';
import { caloriesValidation, emptyNameValidation, emptyNumberValidation, emptyPriceValidation, sameNameValidation } from 'src/utils/validation';
import { defaultItem, defaultItemValidation, defaultItemsFilters } from 'src/view/screens/MenuManagementScreen/screens/ItemsScreen/constants/items';
import {
  IAllergen,
  IItemModifierOptionValidation,
  IItemModifierValidation,
  IItemSizeValidation,
  IItemValidation,
  IItemsFilters,
} from 'src/view/screens/MenuManagementScreen/screens/ItemsScreen/types/items';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';

class ItemsStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;
  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<IProductV4> = defaultPagination;
  @observable item: IProductV4 | null = null;
  @observable allergens: IPickerOption[] = [];
  @observable allergensOptions: IPickerOption[] = [];
  @observable edited: boolean = false;
  @observable filters: IItemsFilters = defaultItemsFilters;
  @action setFilters = (filters: Partial<IItemsFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchItems(1);
  };

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action public fetchData = async (itemId: number) => {
    if (itemId === 0) {
      this.setItem();
      return;
    }

    if (itemId && !this.item?.id) {
      this.setItem(this.pagination.data.find((item) => item.id === itemId));
    }

    await this.getItem(itemId);

    if (this.item?.id) {
      return;
    }

    this.setItem();
  };

  @action fetchItems = async (page: number = this.pagination.current_page + 1) => {
    try {
      const cancelId = 'fetchItems';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      const params: any = {
        page,
        name: this.filters.search,
        is_delete: 0,
        category_id: this.filters.category?.value || undefined,
        sort_field: this.filters.sort_field,
        sort_dir: this.filters.sort_dir,
        types: [PRODUCT_TYPE.PRODUCT],
        only_for_combo: 1,
        ...getAvailabilityParams(this.filters.availability),
      };

      this.pagination.loading = true;
      this.pagination.refreshing = true;
      const pagination = await client.get('v4/products', {
        params,
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination = {
        ...this.pagination,
        loading: false,
        refreshing: false,
      };
      logger.error({ error, place: 'ItemsStore fetchItems' });
    }
  };

  public itemsCount: number | undefined = undefined;
  public foundItemsInSystem: boolean = false;
  @observable donatableItemsCount: number = 0;
  @observable donatableItems: ICountsResponce['donatableItems'] | undefined = undefined;
  public fetchItemsCount = async () => {
    try {
      const counts: ICountsResponce = await client.get(`v4/products_count`, { params: { donatable: 1, only_for_combo: 1 } });

      this.itemsCount = counts.available;
      this.donatableItems = counts.donatableItems?.length ? counts.donatableItems : undefined;
      this.foundItemsInSystem = Boolean(counts.available + counts.deleted);
      this.donatableItemsCount = counts.donatable || 0;
    } catch (error: any) {
      logger.error({ error, place: 'ItemsStore fetchItemsCount' });
    }
  };

  @action getItem = async (id: string | number) => {
    try {
      this.pagination.loading = true;
      const item: IProductV4 = await client.get(`v4/products/${id}`, { headers: { 'Accept-Language': 'en,ar' } });
      this.setItem(item && item.id ? this.transformItem(item) : undefined);
      this.pagination.loading = false;
    } catch (error: any) {
      this.pagination.loading = false;
      logger.error({ error, place: 'ItemsStore getItem', showModal: true });
    }
  };

  @action setItem = (item?: IProductV4) => {
    this.item = item ? this.transformItem(item) : this.defaultItem;

    const allergensIds = new Set(this.item.allergens.map((item) => item.id));
    const selectedPreviouslyAllergens = this.allergensOptions.filter((item) => allergensIds.has(item.value));
    this.allergens = selectedPreviouslyAllergens;

    this.previosModifiersState = this.item.modifiers.slice();

    // set images
    if (this.item.images?.length) {
      this.images = [this.item.image, ...this.item.images];
    } else {
      this.images = this.item.image ? [this.item.image] : undefined;
    }
    // set backup sizes
    if (this.item?.sizes.length) {
      this.backupSizes = this.item.sizes;
    }
    // set backup price
    if (this.item.price) {
      this.backupPrice = this.item.price;
    }

    this.fetchCrossSellingItems();
  };
  private get defaultItem(): IProductV4 {
    const count: string = String((this.itemsCount ?? this.pagination.total) + 1);

    return {
      ...defaultItem,
      name: {
        en: getTranslate('item', LOCALES.EN) + ' ' + count,
        ar: getTranslate('item', LOCALES.AR) + ' ' + count,
      },
    };
  }
  private transformItem = (item: IProductV4): IProductV4 => {
    return {
      ...item,
      external_id: item.external_id?.toString(),
      price: item.price.toString(),
      weight: item.weight.toString(),
      unit: item.unit || MEASURE.NULL,
      modifiers: item.modifiers.length ? item.modifiers.map((modifier) => this.transformModifier(modifier)) : [],
      exclusions: item.exclusions.length ? item.exclusions.map((exclusion) => this.transformExclusion(exclusion)) : [],
      sizes: item.sizes.length ? item.sizes.map((size, index) => this.transformSize(size, index)) : [],
    };
  };
  private transformModifier = (modifier: IProductV4Modifier): IProductV4Modifier => {
    return {
      ...modifier,
      sort: modifier.sort ? modifier.sort : 0,
      external_id: modifier.external_id ? modifier.external_id.toString() : null,
      options: modifier.options.length ? modifier.options.map((option) => this.transformModifierOption(option)) : [],
    };
  };
  private transformModifierOption = (option: IProductV4ModifierOption): IProductV4ModifierOption => {
    return {
      ...option,
      sort: option.sort ? option.sort : 0,
      external_id: option.external_id ? option.external_id.toString() : null,
    };
  };
  private transformExclusion = (exclusion: IProductV4Exclusion): IProductV4Exclusion => {
    return { ...exclusion, external_id: exclusion.external_id ? exclusion.external_id.toString() : null };
  };
  private transformSize = (size: IProductV4Size, index: number): IProductV4Size => {
    return {
      ...size,
      id: Number.isInteger(size.id) ? size.id : undefined,
      price: size.price ? size.price : '0',
      weight: size.weight ? size.weight : '0',
      external_id: size.external_id ? size.external_id.toString() : null,
      sort: index,
    };
  };

  // images block
  @observable images: string[] | undefined = undefined;
  @action uploadImage = (image: string) => {
    if (!this.images) this.images = [image];
    else this.images.push(image);
    this.edited = true;
  };
  @action deleteImage = (image: string) => {
    if (!this.images) return;

    this.images = this.images.filter((_image) => _image !== image);
    this.edited = true;
  };
  @action imagesChangesSubmit = (reset?: boolean) => {
    if (!this.item) return;

    if (reset) {
      this.images = this.item.is_draft ? undefined : [this.item.image, ...(this.item.images || [])];
    } else {
      // this.item = { ...this.item, images: this.images };
    }
  };
  // from DragAndDrop
  @observable draggableImage: string | null = null;
  @action setDraggableImage = (image: string | null) => {
    this.draggableImage = image ? image : null;
  };
  public handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!this.images) return;

    if (over && over.id !== 'image-overlay' && active.id !== over.id) {
      this.images = this.images.map((image) => {
        if (image === over.id) return active.id;
        if (image === active.id) return over.id;
        return image;
      });
      this.edited = true;
    }
    this.setDraggableImage(null);
  };

  private backupPrice: string | null = null;

  // main details block
  @action changeItemOrderType = (orderType: PRODUCT_ORDER_TYPE) => {
    if (!this.item) return;

    if (isOrderTypeSupported(this.item.order_type, orderType)) {
      this.item.order_type -= orderType;
    } else {
      this.item.order_type += orderType;
    }
    this.edited = true;

    // update validation
    if (!this.validation.orderType) this.setValidation();
  };
  @action switchDonatable = (status: TBooleanInNumber) => {
    if (!this.item) return;

    this.item.is_donation = status;
    if (status === 1) this.donatableItemsCount++;
    else this.donatableItemsCount--;
  };
  @action changeItemName = (value: string, currentLanguage: LOCALES) => {
    if (!this.item) return;

    this.item.name = { ...this.item.name, [currentLanguage]: value };
    this.edited = true;
    // update validation
    if (!this.validation.name.enValid || !this.validation.name.arValid) this.setValidation();
  };
  @action changeItemDescription = (value: string, currentLanguage: LOCALES) => {
    if (!this.item) return;

    this.item.description = { ...this.item.description, [currentLanguage]: value };
    this.edited = true;
  };
  @action changeItemHandler = (value: Partial<IProductV4>) => {
    if (!this.item) return;

    this.item = { ...this.item, ...value };
    this.edited = true;
  };

  // sizes block
  private backupSizes: IProductV4Size[] | null = null;
  @action addSize = (first?: boolean) => {
    if (!this.item) return;

    this.item.sizes.push(this.getItemSize(first));
    this.edited = true;
  };
  private getDefaultSizeName = (newSize?: boolean): TTranslate => {
    const count: string = String((this.item?.sizes.length || 0) + (newSize ? 1 : 0));

    return {
      en: getTranslate('txt_size', LOCALES.EN) + ' ' + count,
      ar: getTranslate('txt_size', LOCALES.AR) + ' ' + count,
    };
  };
  private getItemSize = (first?: boolean): IProductV4Size => {
    return {
      id: randomFloatNumber(),
      name: this.getDefaultSizeName(true),
      calories: this.item?.calories || null,
      external_id: '',
      price: first ? this.item?.price || '' : '',
      weight: first ? this.item?.weight || '' : '',
      unit: first ? this.item?.unit || MEASURE.NULL : MEASURE.NULL,
    };
  };
  @action deleteSize = (id?: number) => {
    if (!this.item || !id) return;

    this.item.sizes = this.item.sizes.filter((size) => size.id !== id);
    this.edited = true;
  };
  @action updateSize = (id: number = 0, value: Partial<IProductV4Size>) => {
    if (!this.item || !id) return;
    this.item.sizes = this.item.sizes.map((size) => (size.id === id ? { ...size, ...value } : size));
    this.edited = true;
  };

  // modifiers and exclusions block
  private previosModifiersState: IProductV4Modifier[] = [];
  @action changeModifiers = (modifier: IProductV4Modifier | number) => {
    if (!this.item) return;

    if (typeof modifier === 'number') {
      this.item.modifiers = this.item.modifiers.filter((_modifier) => _modifier.id !== modifier);
      this.previosModifiersState = this.previosModifiersState.filter((_modifier) => _modifier.id !== modifier);
      if (!this.item.modifiers.length) this.item.is_modifiers = 0;
    } else {
      this.item.modifiers.unshift(this.getNewModifier(modifier));
      this.previosModifiersState.push(this.getNewModifier(modifier));
      if (!this.item.is_modifiers) this.item.is_modifiers = 1;
    }
    this.edited = true;
  };
  private getNewModifier = (modifier: IProductV4Modifier): IProductV4Modifier => {
    return {
      ...modifier,
      minimum: 1,
      maximum: 1,
      unique_options: 1,
      multi_choice: 0,
      sort: 0,
      options: modifier.options.length
        ? modifier.options.map((option, index) => {
            return { ...option, is_default: booleanNumberReverse(index), is_active: 1, status: 1, maximum: 1, minimum: 1, sort: 0 };
          })
        : modifier.options,
    };
  };
  @action updateModifier = (id: number, value: Partial<IProductV4Modifier>) => {
    if (!this.item || !this.item.modifiers.length) return;

    let modifier: IProductV4Modifier | undefined = this.item.modifiers.find((_modifier) => _modifier.id === id);
    if (!modifier) return;

    modifier = { ...modifier, ...value };

    this.item.modifiers = this.item.modifiers.map((_modifier) => (_modifier.id === id && modifier ? modifier : _modifier));
    this.updatePreviosModifierState(modifier, id);
    this.edited = true;
  };
  @action updateModifierSingleChoise = (id: number, value: TBooleanInNumber) => {
    if (!this.item || !this.item.modifiers.length) return;

    let modifier: IProductV4Modifier | undefined = this.item.modifiers.find((_modifier) => _modifier.id === id);
    if (!modifier) return;

    if (value === 0) {
      modifier = {
        ...modifier,
        multi_choice: value,
        unique_options: 1,
        minimum: 1,
        maximum: 1,
        options:
          modifier.options?.map((option, index) => {
            return { ...option, is_default: booleanNumberReverse(index), maximum: 1 };
          }) || [],
      };
    } else {
      const previosModifierState: IProductV4Modifier | undefined = this.previosModifiersState.find((_modifier) => _modifier.id === id);
      if (previosModifierState) {
        modifier = {
          ...modifier,
          multi_choice: value,
          unique_options: previosModifierState.unique_options,
          minimum: previosModifierState.minimum,
          maximum: previosModifierState.maximum,
          options:
            modifier.options?.map((option) => {
              const previousModifierOptionState: IProductV4ModifierOption | undefined = previosModifierState.options.find(
                (_option) => _option.id === option.id
              );
              if (previousModifierOptionState)
                return { ...option, is_default: previousModifierOptionState.is_default, maximum: previousModifierOptionState.maximum };
              return option;
            }) || [],
        };
        // update validation
        this.setValidation();
      } else {
        modifier = { ...modifier, multi_choice: value };
      }
    }
    this.item.modifiers = this.item.modifiers.map((_modifier) => (_modifier.id === id && modifier ? modifier : _modifier));
    this.updatePreviosModifierState(modifier, id);
    this.edited = true;
  };
  @action updateModifierUniqueOptions = (id: number, value: TBooleanInNumber) => {
    if (!this.item || !this.item.modifiers.length) return;

    let modifier: IProductV4Modifier | undefined = this.item.modifiers.find((_modifier) => _modifier.id === id);
    if (!modifier) return;

    if (value === 1) {
      modifier = {
        ...modifier,
        unique_options: value,
        options:
          modifier.options?.map((option) => {
            return { ...option, maximum: 1 };
          }) || [],
      };
    } else {
      const previosModifierState: IProductV4Modifier | undefined = this.previosModifiersState.find((_modifier) => _modifier.id === id);
      if (previosModifierState) {
        modifier = {
          ...modifier,
          unique_options: value,
          options:
            modifier.options?.map((option) => {
              const previousModifierOptionState: IProductV4ModifierOption | undefined = previosModifierState.options.find(
                (_option) => _option.id === option.id
              );
              return { ...option, maximum: previousModifierOptionState ? previousModifierOptionState.maximum : 1 };
            }) || [],
        };
        // update validation
        this.setValidation();
      } else {
        modifier = { ...modifier, unique_options: value };
      }
    }

    this.item.modifiers = this.item.modifiers.map((_modifier) => (_modifier.id === id && modifier ? modifier : _modifier));
    this.updatePreviosModifierState(modifier, id);
    this.edited = true;
  };

  @action updateModifierOption = (modifierId: number, id: number, value: Partial<IProductV4ModifierOption>) => {
    if (!this.item || !this.item.modifiers.length) return;

    const modifier: IProductV4Modifier | undefined = this.item.modifiers.find((_modifier) => _modifier.id === modifierId);
    if (!modifier || !modifier.options.length) return;

    if (!modifier.multi_choice && Object.keys(value).includes('is_default')) {
      modifier.options = modifier.options.map((option) => {
        if (option.id === id) return { ...option, is_default: 1 };
        return { ...option, is_default: 0 };
      });
    } else {
      modifier.options = modifier.options.map((option) => {
        if (option.id === id) return { ...option, ...value };
        return option;
      });
    }

    this.item.modifiers = this.item.modifiers.map((_modifier) => (_modifier.id === modifierId ? modifier : _modifier));
    this.updatePreviosModifierState(modifier, modifierId);
    this.edited = true;
  };
  private updatePreviosModifierState = (modifier: IProductV4Modifier, id: number) => {
    this.previosModifiersState = this.previosModifiersState.map((_modifier) =>
      _modifier.id === id && modifier
        ? {
            ...modifier,
            unique_options: !modifier.multi_choice ? _modifier.unique_options : modifier.unique_options,
            minimum: !modifier.multi_choice ? _modifier.minimum : modifier.minimum,
            maximum: !modifier.multi_choice ? _modifier.maximum : modifier.maximum,
            options: modifier.unique_options ? _modifier.options : modifier.options,
          }
        : _modifier
    );
  };
  @action changeExclusions = (exclusion: IProductV4Exclusion | number) => {
    if (!this.item) return;

    if (typeof exclusion === 'number') {
      this.item.exclusions = this.item.exclusions.filter((_exclusion) => _exclusion.id !== exclusion);
      if (!this.item.exclusions.length) this.item.is_exclusions = 0;
    } else {
      this.item.exclusions.unshift(exclusion);
      if (!this.item.is_exclusions) this.item.is_exclusions = 1;
    }
    this.edited = true;
  };

  // cross-selling block
  @observable crossSellingItems: TCategoryProduct[] | undefined = undefined;
  @action fetchCrossSellingItems = async () => {
    try {
      if (this.item?.is_draft || !this.item?.upsales_items.length) return;

      const cancelId = 'fetchCrossItems';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      const crossItems = await client.get('v3/products', {
        params: { products: this.item?.upsales_items },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.crossSellingItems = crossItems;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'ItemsStore fetchCrossSellingItems' });
    }
  };
  @action addCrossSellingItems = (items: IProductV4[]) => {
    if (this.item) this.item.upsales_items = items.map((item) => item.id) || [];
    this.crossSellingItems = items.map((item) => {
      return { id: item.id, name: item.name, image: item.image, sort: item.sort || 0 };
    });
    this.edited = true;
  };
  @action deleteCrossSellingItem = (id: number) => {
    if (!this.crossSellingItems || !this.item?.upsales_items) return;

    this.item.upsales_items = this.item?.upsales_items.filter((item) => item !== id) || [];
    this.crossSellingItems = this.crossSellingItems.filter((item) => item.id !== id);
    this.edited = true;
  };
  // cross-selling block end

  @action createItem = async () => {
    try {
      if (!this.item) return;

      this.pagination.loading = true;

      await client.post(`v4/products`, this.params);

      this.edited = false;
      history.push(SCREENS.ITEMS());

      toastSuccess({ message: localeStore.t('toast_item_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.ITEM, error });
      if (!this.validation.valid) {
        scrollToErrorElement();
        return;
      }

      logger.error({ error, place: 'ItemsStore createItem', showModal: true });
    }
  };

  @action updateItem = async (id: number) => {
    try {
      if (!this.item) return;

      this.pagination.loading = true;

      await client.put(`v4/products/${id}`, this.params);

      this.edited = false;
      history.push(SCREENS.ITEMS());

      toastSuccess({ message: localeStore.t('toast_item_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.ITEM, error });
      if (!this.validation.valid) {
        scrollToErrorElement();
        return;
      }

      logger.error({ error, place: 'ItemsStore updateItem', showModal: true });
    }
  };
  private get params() {
    if (!this.item) return {};

    return {
      ...this.item,
      id: this.item.id || undefined,
      external_id: this.item.external_id || null,
      category_id: this.item.category?.id,
      weight: this.weightParameter,
      price: this.priceParameter,
      sort: Number(this.item.sort) || 0,
      image: this.images?.length ? this.images[0] : null,
      images: this.images?.slice(1),
      min_quantity: this.item.min_quantity ? this.item.min_quantity : 1,
      modifiers: this.item.modifiers.length ? this.item.modifiers.map((modifier) => this.transformModifier(modifier)) : [],
      exclusions: this.item.exclusions.length ? this.item.exclusions.map((exclusion) => this.transformExclusion(exclusion)) : [],
      sizes: this.sizesParameter,
      upsales_items: this.crossSellingItems ? this.crossSellingItems.map((item) => item.id) : undefined,
      is_draft: undefined,
      types: [this.item.type],
      allergens: this.allergens.map((item) => item.value),
      nutrients: this.item.nutrients,
      nutrient: undefined,
    };
  }
  private get priceParameter() {
    if (this.item?.price) return this.item.price;
    if (this.backupPrice) return this.backupPrice;
    if (this.item?.is_size && this.item.sizes.length) return this.item.sizes[0].price;
    return undefined;
  }
  private get weightParameter() {
    if (this.item?.weight) return this.item.weight;
    if (this.item?.is_size && this.item.sizes.length) return this.item.sizes[0].weight || 0;
    return 0;
  }
  private get sizesParameter(): IProductV4Size[] {
    if (!this.item) return [];
    if (this.item.is_size) return this.item.sizes.map((size, index) => this.transformSize(size, index));

    const sizes: IProductV4Size[] = this.item.sizes.map((size, index) => {
      const isPrice: boolean = Boolean(size.price && (Number(size.price) || Number(size.price) === 0));
      const isName = {
        en: Boolean(size.name.en.trim()),
        ar: Boolean(size.name.ar.trim()),
      };

      // if one of required fields is invalid, then we try to find a backup
      const backupSize: IProductV4Size | undefined =
        !isPrice || !isName[LOCALES.EN] || !isName[LOCALES.AR] ? this.backupSizes?.find((_size) => _size.id === size.id) : undefined;

      const getName = (locale: LOCALES.EN | LOCALES.AR, _size: IProductV4Size): string => {
        return isName[locale] ? _size.name[locale] : backupSize?.name[locale] || this.getDefaultSizeName()[locale];
      };

      return this.transformSize(
        {
          ...size,
          price: isPrice ? size.price : backupSize?.price || this.item?.price || '0',
          name: {
            en: getName(LOCALES.EN, size),
            ar: getName(LOCALES.AR, size),
          },
        },
        index
      );
    });

    if (sizes.length) return sizes;
    if (this.backupSizes?.length) return this.backupSizes.map((size, index) => this.transformSize(size, index));
    return [];
  }

  @action changeStock = async (id: number, stock: TBooleanInNumber) => {
    try {
      const cancelId = `stock_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');
      stock = booleanNumberReverse(stock);

      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, out_of_stock: stock } : item));

      await client.put(
        `v4/products/${id}`,
        { out_of_stock: stock, apply_availability_to_all: 1 },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );

      toastSuccess({ message: localeStore.t('toast_item_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((item) =>
        item.id === id ? { ...item, out_of_stock: booleanNumberReverse(item.out_of_stock) } : item
      );
      logger.error({ error, place: 'ItemsStore changeStock' });
    }
  };

  @action changeStatus = async (id: number, status: TBooleanInNumber) => {
    try {
      const cancelId = `status_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status } : item));

      await client.put(
        `v4/products/${id}`,
        { status, apply_availability_to_all: 1 },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );

      toastSuccess({ message: localeStore.t('toast_item_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status: booleanNumberReverse(item.status) } : item));
      logger.error({ error, place: 'ItemsStore changeStatus' });
    }
  };

  @action restoreItem = async (id: number) => {
    try {
      const cancelId = `restore_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.patch(`v4/products/${id}`, {}, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      this.fetchItems(this.pagination.current_page);
      this.itemsCount = countsUpdate(this.itemsCount, true);

      toastSuccess({ message: localeStore.t('toast_item_restored') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'ItemsStore restoreItem', showModal: true });
    }
  };

  @action deleteItem = async (id: number) => {
    try {
      const cancelId = `delete_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.delete(`v4/products/${id}`, {
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.fetchItems(this.pagination.current_page);
      this.itemsCount = countsUpdate(this.itemsCount);

      toastSuccess({ message: localeStore.t('toast_item_deleted') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'ItemsStore deleteItem', showModal: true });
    }
  };

  @observable validation: IItemValidation = defaultItemValidation;
  @action setValidation = (deleteSizes: boolean = false) => {
    if (!this.item) return;

    this.validation = { ...defaultItemValidation };

    // images validation
    // if (!this.images?.length) {
    //   this.validation.images = false;
    //   this.validation.valid = false;
    // }

    // order type validation
    if (!this.item.order_type) {
      this.validation.valid = false;
      this.validation.orderType = false;
      this.validation.orderTypeErrorText = localeStore.t('atleast_one_order_type');
    }

    // name validation
    [this.validation.name, this.validation.valid] = emptyNameValidation(this.item.name, this.validation.name, this.validation.valid);

    // category validation
    if (!this.item.category) {
      this.validation.valid = false;
      this.validation.category = false;
      this.validation.categoryErrorText = localeStore.t('required_field_error');
    }

    // price validation
    if (!this.item.is_size || !this.item.sizes.length) {
      [this.validation.price, this.validation.valid] = emptyPriceValidation(this.item.price, this.validation.price, this.validation.valid);
      if (this.validation.price && this.item.is_size && !deleteSizes) this.item.is_size = 0;
    }

    // sizes validation
    if (this.item.is_size && this.item.sizes.length) {
      this.validation.sizes = this.item.sizes.map((size) => {
        const _size: IItemSizeValidation = {
          id: size.id || 0,
          price: { state: true, errorText: undefined },
          calories: { state: true, errorText: undefined },
          name: { enValid: true, arValid: true },
        };
        // sizes calories validation
        [_size.calories, this.validation.valid] = caloriesValidation(size.calories, _size.calories, this.validation.valid);

        // sizes price validation
        [_size.price, this.validation.valid] = emptyPriceValidation(size.price, _size.price, this.validation.valid);

        // sizes name validation
        [_size.name, this.validation.valid] = emptyNameValidation(size.name, _size.name, this.validation.valid);
        // commented out, because https://taker.atlassian.net/browse/RD-944
        // [_size.name, this.validation.valid] = sameNameValidation({
        //   isValid: this.validation.valid,
        //   name: _size.name,
        //   type: ITEM_TYPE.ITEM_SIZE,
        //   itemArray: this.item?.sizes,
        //   item: size,
        // });

        return _size;
      });
    }

    // modifiers validation
    if (this.item.is_modifiers && this.item.modifiers.length) {
      this.validation.modifiers = this.item.modifiers.map((modifier) => {
        const _modifier: IItemModifierValidation = {
          id: modifier.id,
          minimum: true,
          maximum: true,
          minMaxAccordance: true,
          quantitySumm: true,
          options: [],
        };
        let quantitySumm: number = 0;
        let includeInfinityOption: boolean = false;

        // modifier minimum validation
        [_modifier.minimum, this.validation.valid] = emptyNumberValidation(modifier.minimum, _modifier.minimum, this.validation.valid);

        // modifier maximum validation
        [_modifier.maximum, this.validation.valid] = emptyNumberValidation(modifier.maximum, _modifier.maximum, this.validation.valid);

        // modifier minMaxAccordance validation
        if (modifier.minimum > modifier.maximum) {
          this.validation.valid = false;
          _modifier.minMaxAccordance = false;
        }

        // modifier options validation
        if (modifier.options.length) {
          _modifier.options = modifier.options.map((option) => {
            const _option: IItemModifierOptionValidation = { id: option.id, price: true, calories: { state: true }, quantity: true };

            // modifier option price validation
            [_option.price, this.validation.valid] = emptyNumberValidation(option.price, _option.price, this.validation.valid);

            // // modifier option calories validation
            // [_option.calories, this.validation.valid] = caloriesValidation(option.calories, _option.calories, this.validation.valid);

            // modifier option quantity validation
            [_option.quantity, this.validation.valid] = emptyNumberValidation(option.maximum, _option.quantity, this.validation.valid);

            // quantity summ
            if (option.status && !includeInfinityOption) {
              if (option.maximum === 0) includeInfinityOption = true;
              quantitySumm += option.maximum;
            }

            return _option;
          });

          // modifier quantitySumm validate
          if (modifier.minimum > quantitySumm && !includeInfinityOption) {
            this.validation.valid = false;
            _modifier.quantitySumm = false;
          }
        }

        return _modifier;
      });
    }
  };

  @action prepareAllergens = async () => {
    try {
      const response: IAllergen[] = await client.get('v4/allergens');
      if (response?.length) {
        this.allergensOptions = response.map((item) => ({
          label: getTranslate(item.localized_names),
          value: item.id,
        }));
      }
    } catch (error) {
      logger.error({ error, place: 'ItemsStore prepareAllergens' });
    }
  };

  @action applyNutrients = (value: IProductNutrient | null) => {
    if (!this.item) return;

    if (_.isEqual(this.item.nutrient, value)) return;

    this.item = {
      ...this.item,
      nutrients: value,
      nutrient: value,
    };

    this.edited = true;
  };

  @action unMount = () => {
    this.item = null;
    this.images = undefined;
    this.backupSizes = null;
    this.backupPrice = null;
    this.crossSellingItems = undefined;
    this.previosModifiersState = [];
    this.edited = false;
    this.validation = defaultItemValidation;

    if (!window.location.pathname.includes(SCREENS.ITEMS())) this.filters = defaultItemsFilters;
  };
}

const itemsStore = new ItemsStore();

export default itemsStore;
